// frontend/modules/customer-notification/hooks/useNotificationStatistics.ts
import { useCallback, useEffect } from 'react';
import { useNotificationStore } from '../stores/notification-store';
import { showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';

// Import the API functions generated by Orval
import { useGetNotificationStatistics } from '@/shared/api/client/customer-notification-controller';

export const useNotificationStatistics = (
    startDate?: Date | null,
    endDate?: Date | null
) => {
    const { dark } = useTheme();
    
    // Get state and actions from store
    const {
        statistics,
        isLoading,
        error,
        
        setStatistics,
        setIsLoading,
        setError
    } = useNotificationStore();
    
    // API query for notification statistics
    const {
        data: statsData,
        isLoading: isStatsLoading,
        refetch: refetchStats,
        error: statsError
    } = useGetNotificationStatistics({
            startDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
            endDate: endDate ? endDate.toISOString().split('T')[0] : undefined
    });
    
    // Update statistics when data is loaded
    useEffect(() => {
        if (statsData) {
            setStatistics(statsData);
            setError(null);
        }
    }, [statsData, setStatistics, setError]);
    
    // Handle error state
    useEffect(() => {
        if (statsError) {
            console.error('Error loading notification statistics:', statsError);
            setError('Failed to load notification statistics');
            showErrorNotification('Error', 'Failed to load notification statistics', dark);
        }
    }, [statsError, setError, dark]);
    
    // Set overall loading state
    useEffect(() => {
        setIsLoading(isStatsLoading);
    }, [isStatsLoading, setIsLoading]);
    
    // Reload notification statistics
    const refreshStatistics = useCallback(() => {
        refetchStats();
    }, [refetchStats]);
    
    return {
        // State
        statistics,
        isLoading,
        error,
        
        // Actions
        refreshStatistics
    };
};
